import { ComponentProps, useCallback, useState } from 'react';

import { AxiosError } from 'axios';
import { showModal } from 'modal.react.js';
import { T, uniqBy } from 'ramda';
import { FieldValues } from 'react-hook-form';

import { getSampleListByItineraryId, getSampleListBySampleItineraryId } from 'api/mergeItineraries';
import Preview from 'components/Pages/Admin/shared/Preview';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { Select } from 'components/shared/Select/adapters/HookForm';
import { SafariContentOption } from 'components/shared/Select/options/SafariContent';
import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import { showSnackbar } from 'hooks/useSnackbar';
import { queryClient } from 'providers/AppQueryClientProvider';
import { fetchSampleItinerariesItemThunk } from 'store/itinerariesItem/thunks';
import { createOptionByKeys } from 'utils/createOption';

type Option = {
  label: string;
  value: string;
  subtitle?: string;
};
type SelectProps<_Values extends FieldValues> = typeof Select<_Values, Option, false>;
type Props<Values extends FieldValues> = ComponentProps<SelectProps<Values>> & {
  itineraryId: number;
  isSample: boolean;
};

export const SelectSample = <Values extends FieldValues>({ isSample, itineraryId, ...rest }: Props<Values>) => {
  const [open, setOpen] = useState(false);

  const openPagePreview = async (pageId: number) => {
    setOpen(true);
    await showModal(area, (res) => (
      <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
        <Preview pageId={pageId} fetchData={fetchSampleItinerariesItemThunk} getIsSamplePreview={T} />
      </Modal>
    ));
    setOpen(false);
  };

  const OptionMemoized = useCallback((props: any) => {
    return (
      <SafariContentOption
        hasPreview={true}
        onPreview={() => {
          openPagePreview(props.value);
        }}
        hasContentIndicator={props.data.showContentIndicator}
        shared={props.data?.shared}
        {...props}
      />
    );
  }, []);

  const loadOptions = async (search: string) => {
    try {
      const request = isSample ? getSampleListByItineraryId : getSampleListBySampleItineraryId;

      const response = await queryClient.fetchQuery({
        queryKey: ['sampleProposals'],
        queryFn: () => request(itineraryId, search),
      });

      if (!response) {
        return [];
      }

      return uniqBy(
        (template: Option) => template.value,
        response.data
          .map((template) => createOptionByKeys({ entity: template, labelKey: 'title', subtitleKey: 'notes' }))
          .map((option) => ({
            ...option,
            ...(!!option.subtitle && {
              subtitle: `Notes: ${option.subtitle}`,
            }),
          })) as Option[],
      );
    } catch (err: unknown) {
      const error = err as AxiosError;
      if (error.code === 'ERR_CANCELED') {
        return [];
      }

      showSnackbar({
        text: 'Oops... Problems with getting itinerary templates',
        type: SNACKBAR_SKIN.ERROR,
        uniqueId: 'List itinerary templates',
      });
      return [];
    }
  };

  return (
    <Select
      {...rest}
      placeholder="Select Itinerary"
      lockMenu={open}
      async
      isSearchable
      isClearable
      optionToString
      debounced
      debounceTimeout={300}
      loadOptions={loadOptions}
      components={{
        Option: OptionMemoized,
      }}
    />
  );
};
